import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const ROUNDS = [
  {
    title: "FastAPI Route",
    lines: [
      `from fastapi import FastAPI`,
      `app = FastAPI()`,
      ``,
      `@app.get("/users/{user_id}")`,
      `async def get_user(user_id: int):`,
      `    users = {"1": "Alice", "2": "Bob"}`,
      `    return {"user": users[user_id]}`,
    ],
    bug: 6,
    explanation: "user_id is an int but dict keys are strings — users[user_id] raises KeyError. Fix: users[str(user_id)]",
  },
  {
    title: "Async Mistake",
    lines: [
      `import asyncio`,
      ``,
      `async def fetch_all():`,
      `    results = []`,
      `    for url in urls:`,
      `        data = asyncio.create_task(fetch(url))`,
      `        results.append(data)`,
      `    return results`,
    ],
    bug: 5,
    explanation: "create_task schedules but doesn't await. You need to await asyncio.gather(*results) or await each task.",
  },
  {
    title: "Redis Cache Check",
    lines: [
      `import redis`,
      ``,
      `client = redis.Redis(host='localhost', port=6379)`,
      `client.set('key', 'value', ex=3600)`,
      ``,
      `cached = client.get('key')`,
      `if cached == 'value':`,
      `    print('Cache hit!')`,
    ],
    bug: 6,
    explanation: "Redis returns bytes, not str. cached is b'value', not 'value'. Fix: cached == b'value' or cached.decode().",
  },
  {
    title: "Cosine Similarity",
    lines: [
      `def cosine_similarity(a, b):`,
      `    dot = sum(x * y for x, y in zip(a, b))`,
      `    mag_a = sum(x**2 for x in a)`,
      `    mag_b = sum(x**2 for x in b)`,
      `    return dot / (mag_a * mag_b)`,
    ],
    bug: 4,
    explanation: "Missing sqrt. Cosine similarity = dot / (sqrt(mag_a) * sqrt(mag_b)). Use math.sqrt() or ** 0.5.",
  },
  {
    title: "LangChain Embeddings",
    lines: [
      `from langchain_openai import OpenAIEmbeddings`,
      ``,
      `embedder = OpenAIEmbeddings()`,
      `texts = ['hello', 'world']`,
      `embeddings = embedder.embed_documents(texts)`,
      `result = embeddings[2]`,
      `print(result)`,
    ],
    bug: 5,
    explanation: "Index out of range. texts has 2 items → embeddings has indices 0 and 1. embeddings[2] raises IndexError.",
  },
];

export default function DebugTheCode({ onDone }) {
  const [idx, setIdx]       = useState(0);
  const [score, setScore]   = useState(0);
  const [picked, setPicked] = useState(null);

  const round = ROUNDS[idx];

  const guess = (lineIdx) => {
    if (picked !== null) return;
    const correct = lineIdx === round.bug;
    if (correct) setScore(s => s + 1);
    setPicked({ lineIdx, correct });
  };

  const next = () => {
    if (idx + 1 >= ROUNDS.length) { onDone(score); return; }
    setIdx(i => i + 1);
    setPicked(null);
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16, color: "var(--text-muted)", fontSize: "0.8rem" }}>
        <span>{round.title} · Round {idx + 1}/{ROUNDS.length}</span>
        <span>Score: {score}</span>
      </div>

      <p style={{ fontSize: "0.8rem", color: "var(--text-muted)", marginBottom: 10 }}>Click the buggy line:</p>

      <AnimatePresence mode="wait">
        <motion.div key={idx} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
          <div style={{ background: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: 12, overflow: "hidden", marginBottom: 16, fontFamily: "monospace", fontSize: "0.8rem" }}>
            {round.lines.map((line, i) => {
              const isCorrect = picked && i === round.bug;
              const isWrong   = picked && picked.lineIdx === i && !picked.correct;
              const bg = isCorrect ? "rgba(34,197,94,0.15)" : isWrong ? "rgba(239,68,68,0.15)" : "transparent";
              return (
                <div key={i} onClick={() => guess(i)}
                  style={{
                    display: "flex", alignItems: "flex-start", gap: 12,
                    padding: "5px 16px", cursor: picked ? "default" : (line.trim() ? "pointer" : "default"),
                    background: bg,
                    transition: "background 120ms ease",
                  }}
                  onMouseEnter={e => { if (!picked && line.trim()) e.currentTarget.style.background = "rgba(255,255,255,0.04)"; }}
                  onMouseLeave={e => { if (!picked) e.currentTarget.style.background = "transparent"; }}
                >
                  <span style={{ color: "var(--text-muted)", userSelect: "none", minWidth: 20, textAlign: "right", opacity: 0.5 }}>{i + 1}</span>
                  <span style={{ color: "var(--text)", whiteSpace: "pre" }}>{line || " "}</span>
                  {isCorrect && <span style={{ marginLeft: "auto", color: "#22c55e" }}>← bug</span>}
                </div>
              );
            })}
          </div>

          {picked && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <div style={{
                padding: "12px 16px", borderRadius: 10, marginBottom: 12, fontSize: "0.825rem",
                background: picked.correct ? "rgba(34,197,94,0.1)" : "rgba(239,68,68,0.1)",
                border: `1px solid ${picked.correct ? "rgba(34,197,94,0.3)" : "rgba(239,68,68,0.3)"}`,
                color: "var(--text)",
              }}>
                {picked.correct ? "Got it. " : `Incorrect — line ${round.bug + 1} was the bug. `}
                <span style={{ color: "var(--text-muted)" }}>{round.explanation}</span>
              </div>
              <button className="btn-pill" onClick={next} style={{ width: "100%", justifyContent: "center" }}>
                {idx + 1 >= ROUNDS.length ? "See Results" : "Next →"}
              </button>
            </motion.div>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
